import { ReactNode, useId, useLayoutEffect, useRef, useState } from 'react';
import { ComboBox, Input, ListBox, ListBoxItem, Popover } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClientFormDrawer } from '@/components/ClientForm/ClientFormDrawer';
import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCancel from '@/icons/Cancel.svg';
import IconSearch from '@/icons/Search.svg';
import { ClientFromGetMany, useClients } from '@/models/client';
import { formatPhone } from '@/models/partials/phone';
import { createBEMClasses } from '@/utils/classname';
import useDebouncedState, { useDebouncedValue } from '@/utils/useDebouncedState';

import './ClientSearchInput.css';

const { block, element } = createBEMClasses('client-search-input');

export const ClientSearchInput = ({
  setSelectedClient,
}: {
  setSelectedClient: (client?: ClientFromGetMany) => void;
}) => {
  const { t } = useLingui();

  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isCreateClientDrawerOpen, setIsCreateClientDrawerOpen] = useState(false);

  const [code, debouncedCode, setCode] = useDebouncedState('', 300);
  const [firstName, debouncedFirstName, setFirstName] = useDebouncedState('', 300);
  const [lastName, debouncedLastName, setLastName] = useDebouncedState('', 300);
  const [postalCode, debouncedPostalCode, setPostalCode] = useDebouncedState('', 300);

  const { data: { clients } = { clients: [] }, isFetching } = useClients({
    code: debouncedCode,
    firstName: debouncedFirstName,
    lastName: debouncedLastName,
    postalCode: debouncedPostalCode,
    limit: 5,
  });

  useLayoutEffect(() => {
    setTriggerWidth(triggerRef.current?.clientWidth ?? 0);
  }, []);

  const isLoading =
    isFetching &&
    !!(debouncedCode || debouncedFirstName || debouncedLastName || debouncedPostalCode);
  const isLoadingDebounced = useDebouncedValue(isLoading, 300);
  const shouldShowLoading = isLoading && isLoadingDebounced;

  return (
    <div className={block()}>
      <ComboBox
        items={clients}
        allowsEmptyCollection
        menuTrigger="focus"
        shouldFocusWrap
        aria-label={t({ id: 'client-search-input.aria-label', message: 'Client search' })}
        onChange={(key) => setSelectedClient(clients.find((c) => c.externalId === key))}
      >
        <div ref={triggerRef} className={element('inputs')}>
          <Button variant="style-less" isLoading={shouldShowLoading}>
            <IconSearch />
          </Button>
          <FieldInput
            label={<Trans id="client-search-input.code.label">Code:</Trans>}
            value={code}
            onChange={setCode}
          />
          <FieldInput
            label={<Trans id="client-search-input.last-name.label">Last name:</Trans>}
            value={lastName}
            onChange={setLastName}
          />
          <FieldInput
            label={<Trans id="client-search-input.first-name.label">First name:</Trans>}
            value={firstName}
            onChange={setFirstName}
          />
          <FieldInput
            label={<Trans id="client-search-input.postal-code.label">Postal code:</Trans>}
            value={postalCode}
            onChange={setPostalCode}
          />
        </div>
        <Popover triggerRef={triggerRef} offset={4}>
          <ListBox
            className={element('results')}
            style={{
              width: `${triggerWidth ?? 0}px`,
            }}
            items={clients}
          >
            {(client) => (
              <ListBoxItem
                id={client.externalId}
                className={element('result')}
                textValue={client.name}
              >
                <Paragraph weight="medium">{client.name ?? '-'}</Paragraph>
                <Paragraph size="300" color="secondary">
                  {[client.email, formatPhone(client.phone), client.address?.zipCode]
                    .filter(Boolean)
                    .join(' • ')}
                </Paragraph>
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>

      <Paragraph size="200" color="secondary">
        <Trans id="client-search-input.cant-find-them">
          Can&apos;t find them?{' '}
          <Link to="#" onClick={() => setIsCreateClientDrawerOpen(true)}>
            Create a new client
          </Link>
        </Trans>
      </Paragraph>

      <ClientFormDrawer
        isOpen={isCreateClientDrawerOpen}
        setIsOpen={setIsCreateClientDrawerOpen}
        hideDeliveryOption
        customTitle={
          <Trans id="client-search-input.create-client-drawer.title">Create a new client</Trans>
        }
      />
    </div>
  );
};

const FieldInput = ({
  label,
  value,
  onChange,
}: {
  label: ReactNode;
  value: string;
  onChange: (newValue: string) => void;
}) => {
  const id = useId();
  const { t } = useLingui();

  return (
    <div className={element('field-input')}>
      <label id={id}>{label}</label>
      {value && (
        <button
          onClick={() => onChange('')}
          aria-label={t({
            id: 'client-search-input.field-input.clear-button.aria-label',
            message: 'Clear input',
          })}
        >
          <IconCancel />
        </button>
      )}
      <Input
        aria-labelledby={id}
        value={value}
        autoComplete="off"
        onChange={(evt) => onChange(evt.target.value)}
        placeholder={t({
          id: 'client-search-input.field-input.placeholder',
          message: 'Search...',
        })}
      />
    </div>
  );
};
